import { Color, ColorSpecification, DataDrivenPropertyValueSpecification, ExpressionSpecification } from '@maplibre/maplibre-gl-style-spec';
import {
    CategoricalIndicatorPalette,
    CategoricalIndicatorPaletteWithValues,
    CombinedPalette,
    DiscretePalette,
    GradientPalette,
    Palette
} from '../palettes/palettes';

export function generateRampFromPalette(palette: Palette): Uint8Array {
    switch (palette.type) {
        case 'categorical':
            return categoricalRamp(palette);
        case 'discreteBar':
            return discreteRamp(palette);
        case 'combined':
            return combinedRamp(palette);
        case 'gradientBar':
            return gradientRamp(palette);
    }
}

export function generateColorExpressionFromPalette(
    palette: Palette,
    compareExpression: ExpressionSpecification,
    defaultColor?: string
): DataDrivenPropertyValueSpecification<ColorSpecification> {
    let length = 0;
    switch (palette.type) {
        case 'categorical':
            length = palette.entries.length;
            break;
        case 'discreteBar':
            length = palette.colors.length;
            break;
        case 'combined':
            length = palette.colors.length;
            break;
        case 'gradientBar':
            length = 256;
            break;
    }

    const ramp = generateRampFromPalette(palette);
    const colors: Record<number, string> = {};
    for (let i = 0; i < length; i++) {
        if (palette.type === 'categorical' && palette.withValues) {
            colors[palette.entries[i].value] = `rgb(${ramp[i * 4 + 0]},${ramp[i * 4 + 1]},${ramp[i * 4 + 2]})`;
        } else {
            colors[i] = `rgb(${ramp[i * 4 + 0]},${ramp[i * 4 + 1]},${ramp[i * 4 + 2]})`;
        }
    }

    return [
        'case',
        ...Object.entries(colors)
            .map((x) => [['==', compareExpression, +x[0]], x[1]])
            .flat(1),
        defaultColor ?? 'rgba(0,0,0,0)'
    ] as DataDrivenPropertyValueSpecification<ColorSpecification>;
}
function discreteRamp(palette: DiscretePalette): Uint8Array {
    const ramp = new Uint8Array(256 * 4);
    for (let i = 0; i < palette.colors.length; i++) {
        const color = Color.parse(palette.colors[i])!;
        ramp[i * 4 + 0] = color.r * 0xff;
        ramp[i * 4 + 1] = color.g * 0xff;
        ramp[i * 4 + 2] = color.b * 0xff;
        ramp[i * 4 + 3] = color.a * 0xff;
    }

    return ramp;
}

function combinedRamp(palette: CombinedPalette): Uint8Array {
    const ramp = new Uint8Array(256 * 4);
    let i = 0;
    for (const pal of palette.palettes) {
        for (let j = 0; j < pal.length; j++) {
            const colorStr = palette.colors[pal.offset + j];
            const color = Color.parse(colorStr)!;
            ramp[i * 4 + 0] = color.r * 0xff;
            ramp[i * 4 + 1] = color.g * 0xff;
            ramp[i * 4 + 2] = color.b * 0xff;
            ramp[i * 4 + 3] = color.a * 0xff;
            i++;
        }
    }

    return ramp;
}

function categoricalRamp(palette: CategoricalIndicatorPalette | CategoricalIndicatorPaletteWithValues): Uint8Array {
    const ramp = new Uint8Array(256 * 4);
    for (let i = 0; i < palette.entries.length; i++) {
        const color = Color.parse(palette.entries[i].color)!;
        ramp[i * 4 + 0] = color.r * 0xff;
        ramp[i * 4 + 1] = color.g * 0xff;
        ramp[i * 4 + 2] = color.b * 0xff;
        ramp[i * 4 + 3] = color.a * 0xff;
    }
    return ramp;
}

function gradientRamp(palette: GradientPalette): Uint8Array {
    const ramp = new Uint8Array(256 * 4);

    const range = palette.maxValue - palette.minValue;

    const firstStop = palette.stops[0];

    if (firstStop.value !== palette.minValue) {
        const color = Color.parse(firstStop.color)!;
        for (let i = 0; i < ((firstStop.value - palette.minValue) / range) * 255; i++) {
            ramp[i * 4 + 0] = color.r * 255;
            ramp[i * 4 + 1] = color.g * 255;
            ramp[i * 4 + 2] = color.b * 255;
            ramp[i * 4 + 3] = 255;
        }
    }

    let previousStop = palette.stops[0];
    let previousColor = Color.parse(previousStop.color)!;
    for (let i = 1; i < palette.stops.length; i++) {
        const stop = palette.stops[i];
        const color = Color.parse(palette.stops[i].color)!;

        const from = Math.floor(((previousStop.value - palette.minValue) / range) * 255);
        const to = Math.floor(((stop.value - palette.minValue) / range) * 255);
        const stopRange = to - from;

        for (let j = from; j <= to; j++) {
            const newColor = Color.interpolate(previousColor, color, (j - from) / stopRange);
            ramp[j * 4 + 0] = newColor.r * 255;
            ramp[j * 4 + 1] = newColor.g * 255;
            ramp[j * 4 + 2] = newColor.b * 255;
            ramp[j * 4 + 3] = 255;
        }

        previousStop = stop;
        previousColor = color;
    }

    const lastStop = palette.stops[palette.stops.length - 1];
    if (lastStop.value !== palette.maxValue) {
        const color = Color.parse(lastStop.color)!;
        for (let i = Math.floor(((lastStop.value - palette.minValue) / range) * 255); i < 255; i++) {
            ramp[i * 4 + 0] = color.r * 255;
            ramp[i * 4 + 1] = color.g * 255;
            ramp[i * 4 + 2] = color.b * 255;
            ramp[i * 4 + 3] = 255;
        }
    }

    return ramp;
}
